import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Printer, Download, Check, Anchor, MapPin, Calendar,
  Ship, Package, Container, Truck, Receipt, BadgeCheck,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getBookingDetails, addBookingDetails, updateBookingDetails } from '@/redux/Booking/actions';
import { getAddressAPI } from '@/redux/Purchase/actions';
import {
  getCountryList, getDocumentTypePurchaseList, getDocumentTypeSalesList, getIncoTermsList,
  getQuantityTypeList, getSalesPersonList, getUomTypeList, getCommodityGroup, getContainerType,
  GetOperator, getMoveType, GetCurrencyList, GetChargeBasisList, getUomList,
  getFileManagerList, getMasterCompanyDetails, getClauselist,
} from '@/redux/Common/actions';
import { loaderStart, loaderStop } from '@/redux/Loader/actions';
import { buildBookingPrintDocumentHtml } from './bookingPrintDocumentHtml';
import { baseURL } from '@/config/APIConfig';
import { axiosInstance } from '@/config/NetworkUtility';

const NAVY = '#0F2748';
const HEADER_BG = NAVY;

const INVALID = ['—', '-', '', 'null', 'undefined', '01 Jan 1900 00:00', 'Invalid date'];
const isProvided = (v) => v != null && !INVALID.includes(String(v).trim());

/* ─── UI primitives (from BookingConfirmation8) ───────────── */
function Field({ label, value, strong }) {
  const provided = isProvided(value);
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      {provided ? (
        <div className={`mt-0.5 text-[11.5px] leading-snug ${strong ? 'font-bold text-slate-900' : 'font-semibold text-slate-800'}`}>
          {value}
        </div>
      ) : (
        <div className="mt-0.5 text-[10.5px] italic text-slate-300">Not provided</div>
      )}
    </div>
  );
}

function formatAddressLines(value) {
  if (!isProvided(value)) return [];
  return String(value)
    .split(/\r\n|\n|,/)
    .map((line) => line.trim())
    .filter(Boolean);
}

const normText = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const isDuplicateText = (a, b) => {
  const na = normText(a);
  const nb = normText(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length >= 4 && nb.length >= 4 && (na.includes(nb) || nb.includes(na))) return true;
  return false;
};

/** Drop address lines already shown as name/contact; remove repeats within the block */
function uniqueAddressLines(lines, ...alreadyShown) {
  const seen = new Set();
  const register = (val) => {
    const key = normText(val);
    if (key) seen.add(key);
  };
  alreadyShown.filter(isProvided).forEach(register);

  const out = [];
  for (const line of lines) {
    const key = normText(line);
    if (!key) continue;
    let dup = false;
    for (const s of seen) {
      if (key === s || (key.length >= 4 && s.length >= 4 && (key.includes(s) || s.includes(key)))) {
        dup = true;
        break;
      }
    }
    if (dup) continue;
    seen.add(key);
    out.push(line);
  }
  return out;
}

/** Append Tel/Fax/Attn/Email lines only when provided and not already in the address block */
function mergeContactIntoAddressLines(addressLines = [], contacts = []) {
  const lines = [...addressLines];
  for (const { label, value } of contacts) {
    if (!isProvided(value)) continue;
    const trimmed = String(value).trim();
    const formatted = `${label}: ${trimmed}`;
    const duplicate = lines.some(
      (line) =>
        isDuplicateText(line, trimmed) ||
        isDuplicateText(line, formatted) ||
        (/@/.test(trimmed) && /@/.test(line) && isDuplicateText(line, trimmed)),
    );
    if (!duplicate) lines.push(formatted);
  }
  return lines;
}

function DenseRow({ label, value, strong }) {
  const provided = isProvided(value);
  return (
    <div className="flex items-baseline justify-between gap-3 py-[3px] border-b border-dashed border-slate-100 last:border-b-0">
      <span className="text-[9.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 shrink-0">{label}</span>
      {provided ? (
        <span className={`text-[11px] text-right break-words max-w-[58%] ${strong ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>
          {value}
        </span>
      ) : (
        <span className="text-[10.5px] italic text-slate-300">Not provided</span>
      )}
    </div>
  );
}

function routingPrintValue(v) {
  return isProvided(v) ? String(v).trim() : '—';
}

function formatRoutingDate(d) {
  if (!d || !moment(d).isValid()) return '—';
  return moment(d).format('DD MMM YYYY');
}

function formatLiveLoadStatus(status) {
  if (!isProvided(status)) return '';
  if (status === 'LIVE') return 'Live';
  if (status === 'DROP_PICK') return 'Drop & Pick';
  if (status === 'PENDING') return 'Pending';
  return String(status);
}

function RoutingAddressBlock({ name, addressLines = [] }) {
  const addrLines = addressLines?.length ? addressLines : [];
  if (!isProvided(name) && !addrLines.length) {
    return <div className="text-[10.5px] italic text-slate-300">Not provided</div>;
  }
  return (
    <div className="space-y-0.5">
      {isProvided(name) && (
        <div className="text-[11px] font-bold text-slate-900 leading-snug break-words">{name}</div>
      )}
      {addrLines.map((line, i) => (
        <div key={i} className="text-[11px] font-semibold text-slate-800 leading-snug break-words">
          {line}
        </div>
      ))}
    </div>
  );
}

function RoutingFooterRow({ label, value }) {
  return (
    <div className="mt-auto flex items-baseline justify-between gap-3 border-t border-slate-200 pt-2">
      <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-400 shrink-0">{label}</span>
      <span className="text-[11px] font-bold text-slate-900 tabular-nums text-right">{value}</span>
    </div>
  );
}

function RoutingDetailRow({ label, value }) {
  return <DenseRow label={label} value={routingPrintValue(value)} />;
}

function DomesticRoutingCell({ title, children, footerLabel, footerValue, borderRight, borderBottom }) {
  const cellClass = [
    'domestic-routing-cell flex min-h-[118px] flex-col bg-slate-50/30 p-2.5',
    borderRight ? 'border-r border-slate-200' : '',
    borderBottom ? 'border-b border-slate-200' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cellClass}>
      <div className="text-[8.5px] font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      <div className="flex flex-1 flex-col">{children}</div>
      {footerLabel ? <RoutingFooterRow label={footerLabel} value={footerValue} /> : null}
    </div>
  );
}

function DomesticRoutingPrint({
  pickupTerminal,
  pickupAddressLines = [],
  earliestPickupDate,
  deliveryTerminal,
  deliveryAddressLines = [],
  earliestReturnDate,
  loadingName,
  loadingAddressLines,
  loadingTel,
  loadingFax,
  loadingAttn,
  loadingEmail,
  liveLoad,
  dropDate,
  pickupDate,
  showTrucker,
  truckerName,
  truckerAddressLines,
  truckerCtc,
  truckerTel,
  truckerEmail,
  ctcTrucker,
  rateAgreed,
  rateAgreedForWaiting,
  className = '',
}) {
  const remarksParts = [];
  if (isProvided(rateAgreed)) remarksParts.push(String(rateAgreed).trim());
  if (isProvided(rateAgreedForWaiting)) {
    remarksParts.push(`Wait time / hour: ${String(rateAgreedForWaiting).trim()}`);
  }
  const remarks = remarksParts.join('\n');

  return (
    <SectionCard
      icon={Truck}
      title="Domestic Routing"
      subtitle="Pickup, delivery, loading & trucker details"
      className={className}
    >
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="grid grid-cols-2">
          <DomesticRoutingCell
            title="Pickup Terminal"
            borderRight
            borderBottom
            footerLabel="Earliest Pick-up Date"
            footerValue={formatRoutingDate(earliestPickupDate)}
          >
            <RoutingAddressBlock name={pickupTerminal} addressLines={pickupAddressLines} />
          </DomesticRoutingCell>

          <DomesticRoutingCell
            title="Delivery Terminal"
            borderBottom
            footerLabel="Earliest Return Date"
            footerValue={formatRoutingDate(earliestReturnDate)}
          >
            <RoutingAddressBlock name={deliveryTerminal} addressLines={deliveryAddressLines} />
          </DomesticRoutingCell>

          <DomesticRoutingCell title="Loading Location" borderRight>
            <RoutingAddressBlock
              name={loadingName}
              addressLines={mergeContactIntoAddressLines(loadingAddressLines, [
                { label: 'Tel', value: loadingTel },
                { label: 'Fax', value: loadingFax },
                { label: 'Attn', value: loadingAttn },
                { label: 'Email', value: loadingEmail },
              ])}
            />
            <div className="mt-2.5 border-t border-slate-200 pt-2 space-y-0">
              <RoutingDetailRow label="Live Load" value={liveLoad} />
              <RoutingDetailRow label="Drop Date" value={dropDate} />
              <RoutingDetailRow label="Pickup Date" value={pickupDate} />
            </div>
          </DomesticRoutingCell>

          <DomesticRoutingCell
            title="Trucker/Inland Carrier"
            footerLabel="CTC Trucker"
            footerValue={routingPrintValue(ctcTrucker)}
          >
            {showTrucker ? (
              <RoutingAddressBlock
                name={truckerName}
                addressLines={mergeContactIntoAddressLines(truckerAddressLines, [
                  { label: 'Tel', value: truckerTel },
                  { label: 'Email', value: truckerEmail },
                ])}
              />
            ) : (
              <RoutingAddressBlock name="" addressLines={[]} />
            )}
          </DomesticRoutingCell>
        </div>
      </div>

      {/* <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2">
        <div className="text-[8.5px] font-bold uppercase tracking-widest text-slate-500">Rate Agreed / Remarks</div>
        <div className="mt-0.5 text-[11px] font-semibold text-slate-800 leading-snug whitespace-pre-wrap">
          {isProvided(remarks) ? remarks : <span className="italic text-slate-300">Not provided</span>}
        </div>
      </div> */}
    </SectionCard>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      <header className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-slate-100">
        {Icon && (
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md text-white shrink-0" style={{ backgroundColor: NAVY }}>
            <Icon className="h-3 w-3" strokeWidth={2.4} />
          </span>
        )}
        <div>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-800">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[10px] text-slate-400">{subtitle}</p>}
        </div>
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function ChipMeta({ label, value, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">{label}</span>
      <span className={`mt-0.5 text-[12px] font-bold tabular-nums leading-none text-slate-900 ${mono ? 'font-mono' : ''}`}>
        {isProvided(value) ? value : '—'}
      </span>
    </div>
  );
}

function RouteTimeline({ origin, pol, pod, dest, carrier, vessel, equipment }) {
  const nodes = [
    { label: 'Place of Receipt', value: origin },
    { label: 'Port of Load', value: pol },
    { label: 'Port of Discharge', value: pod },
    { label: 'Final Destination', value: dest },
  ];

  const providedIndices = nodes.map((n, i) => (isProvided(n.value) ? i : -1)).filter((i) => i >= 0);
  const firstProvided = providedIndices[0] ?? -1;
  const lastProvided = providedIndices[providedIndices.length - 1] ?? -1;
  const trackLeft = 6;
  const trackWidth = 88;
  const nodeCenter = (i) => trackLeft + (i / (nodes.length - 1)) * trackWidth;
  const activeTrailStyle =
    firstProvided >= 0 && lastProvided > firstProvided
      ? {
        left: `${nodeCenter(firstProvided)}%`,
        width: `${nodeCenter(lastProvided) - nodeCenter(firstProvided)}%`,
        background: NAVY,
      }
      : null;

  return (
    <div className="relative px-2">
      <div className="absolute top-[8px] left-[6%] right-[6%] h-[2px] bg-slate-200 rounded-full" />
      {activeTrailStyle && (
        <div className="absolute top-[8px] h-[2px] rounded-full" style={activeTrailStyle} />
      )}

      <div className="relative grid grid-cols-4 gap-3 pt-0.5">
        {nodes.map((n, i) => {
          const provided = isProvided(n.value);
          const isShip = i === 1 || i === 2;
          return (
            <div key={i} className="flex flex-col items-center text-center">
              <span className="flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white shadow-sm" style={{ borderColor: provided ? NAVY : '#cbd5e1' }}>
                <span className="block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: provided ? NAVY : '#e2e8f0' }} />
              </span>
              <div className="mt-1.5 inline-flex items-center gap-1 text-[8.5px] font-bold uppercase tracking-widest text-slate-500">
                {isShip ? <Ship className="h-2.5 w-2.5" /> : <MapPin className="h-2.5 w-2.5" />}
                {n.label}
              </div>
              {provided ? (
                <div className="mt-0.5 text-[11px] font-bold text-slate-900 leading-tight">{n.value}</div>
              ) : (
                <div className="mt-0.5 text-[10.5px] italic text-slate-300">Not provided</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
        <div className="flex items-center gap-2">
          <Anchor className="h-3 w-3 text-slate-500 shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="text-[8.5px] font-semibold uppercase tracking-widest text-slate-400">Carrier</div>
            <div className="text-[11px] font-bold text-slate-900 truncate">{isProvided(carrier) ? carrier : '—'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:border-l sm:border-slate-200 sm:pl-3">
          <Ship className="h-3 w-3 text-slate-500 shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="text-[8.5px] font-semibold uppercase tracking-widest text-slate-400">Vessel / Voyage</div>
            <div className="text-[11px] font-bold text-slate-900 truncate">{isProvided(vessel) ? vessel : '—'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:border-l sm:border-slate-200 sm:pl-3">
          <Container className="h-3 w-3 text-slate-500 shrink-0" />
          <div className="leading-tight min-w-0 flex-1">
            <div className="text-[8.5px] font-semibold uppercase tracking-widest text-slate-400">Equipment</div>
            <div className="text-[11px] font-bold text-slate-900 truncate">
              {equipment?.length ? equipment.map((eq) => eq.type).filter(Boolean).join(', ') : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduleGrid({ items }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map((it) => {
        const provided = isProvided(it.value);
        return (
          <div key={it.label} className={`rounded-lg border px-2.5 py-2 ${it.hi ? 'border-sky-100 bg-sky-50/50' : 'border-slate-200 bg-slate-50/40'}`}>
            <div className="flex items-center gap-1.5">
              <Calendar className={`h-2.5 w-2.5 ${it.hi ? 'text-sky-600' : 'text-slate-400'}`} />
              <span className="text-[8.5px] font-bold uppercase tracking-widest text-slate-500">{it.label}</span>
            </div>
            {provided ? (
              <div className={`mt-0.5 text-[11px] font-bold tabular-nums ${it.hi ? 'text-sky-800' : 'text-slate-900'}`}>{it.value}</div>
            ) : (
              <div className="mt-0.5 text-[10px] italic text-slate-300">Not provided</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChargesTable({ containerTypes, chargesByContainer, currency = 'USD' }) {

  const allAR = containerTypes.flatMap((ct) => chargesByContainer[ct]?.AR || []);
  const grandTotal = allAR.reduce((s, i) => s + (Number(i.rate) || 0), 0);

  if (!allAR.length) {
    return <div className="p-4 text-center text-[11px] italic text-slate-400">No charges data available</div>;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-[11.5px]">
        <thead>
          <tr className="bg-slate-50 text-slate-500">
            <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-widest">Description</th>
            <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-widest">Basis</th>
            <th className="px-3 py-2 text-center text-[9px] font-bold uppercase tracking-widest">Qty</th>
            <th className="px-3 py-2 text-left text-[9px] font-bold uppercase tracking-widest">Container</th>
            <th className="px-3 py-2 text-right text-[9px] font-bold uppercase tracking-widest">Amount</th>
            <th className="px-3 py-2 text-right text-[9px] font-bold uppercase tracking-widest">Rate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {allAR.map((c, idx) => (
            <tr key={idx}>
              <td className="px-3 py-2 font-semibold text-slate-900">{c.description || '-'}</td>
              <td className="px-3 py-2 text-slate-600">{c.basis || '-'}</td>
              <td className="px-3 py-2 text-center text-slate-600">{c.quantity?.toLocaleString() || '0'}</td>
              <td className="px-3 py-2 text-slate-600">{c.container === 'GENERAL' ? '—' : c.container}</td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900">
                {(Number(c.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                {(Number(c.rate) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-2 divide-x divide-slate-100 border-t border-slate-100">
        <div className="px-4 py-2 text-right bg-slate-50/50">
          {/* <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Subtotal</div>
          <div className="mt-0.5 text-[12px] font-bold tabular-nums text-slate-900">
            {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
            <span className="text-[9px] font-semibold text-slate-500">{currency}</span>
          </div> */}
        </div>
        <div className="px-4 py-2 text-right bg-emerald-50/70">
          <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-700">Total AR (Sell)</div>
          <div className="mt-0.5 text-[14px] font-black tabular-nums text-emerald-800">
            {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
            <span className="text-[9px] font-bold text-emerald-700">{currency}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrintDisclaimer({ poweredBy }) {
  return (
    <p className="booking-print-disclaimer border-t border-slate-200 pt-1.5 text-center text-[9px] leading-snug text-slate-400">
      This is a computer-generated document. No signature required.
      {' '}Thank you for choosing{' '}
      <span className="font-semibold text-slate-500">{poweredBy || 'Shipez.ai'}</span>
    </p>
  );
}

function AcceptanceFooter({ preparedBy, preparedCo, date }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="grid grid-cols-2 divide-x divide-slate-100">
        <div className="px-3 py-2">
          <div className="text-[8.5px] font-bold uppercase tracking-widest text-slate-400">Prepared By</div>
          <div className="mt-0.5 text-[12px] font-bold text-slate-900">{isProvided(preparedBy) ? preparedBy : '—'}</div>
          <div className="text-[10.5px] text-slate-600 leading-snug">{isProvided(preparedCo) ? preparedCo : ''}</div>
          <div className="text-[9.5px] text-slate-400">{isProvided(date) ? date : ''}</div>
        </div>
        <div className="flex flex-col px-3 py-2">
          <div className="text-[8.5px] font-bold uppercase tracking-widest text-slate-400">Customer Acceptance</div>
          <div className="mt-auto pt-2">
            <div className="border-b border-slate-300" />
            <div className="mt-0.5 text-[9px] text-slate-400 text-center">Authorised Signature &amp; Date</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const BookingPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList,
    salesPersonList, uomTypeList, commodityGroupList, containerTypeList, operatorList, moveTypeList,
    currency_list, charges_basis_list, MasterCompanyDetails,
  } = useSelector((state) => state.common);

  const { id: encodedId } = useParams();
  const bookingid = atob(encodedId);

  const [formData, setFormData] = useState(null);
  const [chargesByContainer, setChargesByContainer] = useState({});
  const [containerTypes, setContainerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();
  const [customerAddress, setCustomerAddress] = useState();
  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');
  const [arCharges, setArCharges] = useState([]);
  const [apCharges, setApCharges] = useState([]);
  const [clauses, setClauses] = useState([]);

  const authName = authUser?.username;
  const companyId = authUser?.companyId;

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (bookingid && bookingid !== 0) {
          loadCommonData();
        } else {
          await _setNewBooking();
        }
      } catch (error) {
        console.error('Error initializing booking:', error);
        await _setNewBooking();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [bookingid]);

  const loadCommonData = async () => {
    dispatch(getMasterCompanyDetails(authUser?.companyId));
    _getBooking();
  };

  const _getBooking = async () => {
    try {
      let response;
      if (typeof bookingid === 'number' || (typeof bookingid === 'string' && bookingid !== '0')) {
        response = await dispatch(getBookingDetails(bookingid, companyId));
        _setAddress('customer', response?.customerId);
        if (response) {
          setFormData(response);
          console.log(response);
          _getTruckerAddress(response?.truckerId);
          processRates(response);
          await getClauselistData(response?.clauses || []);
        } else {
          await _setNewBooking();
        }
      } else if (bookingid && typeof bookingid === 'object') {
        setFormData(bookingid);
        processRates(bookingid);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      await _setNewBooking();
    }
  };

  const getClauselistData = async (formClauses = []) => {
    try {
      const payload = { IPageNo: 1, IPageRecords: 1000, Type: 3 };
      const clauseData = await dispatch(getClauselist(payload));
      const transformedData = clauseData
        .filter((item) => formClauses.some((fc) => fc.clausesTypeDescriptionId === item.ClauseTypeDescriptionID))
        .map((item) => ({
          ClauseTypeDescriptionID: item.ClauseTypeDescriptionID,
          ClauseType: item.ClauseType,
          ClauseDescription: item.ClauseDescription,
          applied: false,
          id: null,
          bookingId: null,
        }));
      setClauses(transformedData);
    } catch (error) { }
  };

  const [truckerAddress, setTruckerAddress] = useState();

  const _getTruckerAddress = async (truckerId) => {
    if (!truckerId) {
      setTruckerAddress('');
      return;
    }
    try {
      const address = await getAddressAPI(truckerId);
      setTruckerAddress(address);
    } catch {
      setTruckerAddress('');
    }
  };

  const _setAddress = async (type, id) => {
    const address = await getAddressAPI(id);
    setCustomerAddress(address);
  };

  const _setNewBooking = async () => { };

  const processRates = (data) => {
    if (!data) { setContainerTypes([]); setChargesByContainer({}); return; }
    const ar = Array.isArray(data.arCharges) ? data.arCharges : [];
    const ap = Array.isArray(data.apCharges) ? data.apCharges : [];
    const normalizedAR = ar.map((item) => ({
      type: 'AR', description: item.description || '', basis: item.basis || '',
      quantity: Number(item.quantity || 0), container: item.container?.trim() || 'GENERAL',
      rate: Number(item.sellRate || 0), amount: Number(item.amount || 0),
    }));
    const normalizedAP = ap.filter((item) => Number(item.amount) > 0).map((item) => ({
      type: 'AP', description: item.description || '', basis: item.basis || '',
      quantity: Number(item.quantity || 0), container: item.container?.trim() || 'GENERAL',
      rate: Number(item.buyRate || 0), amount: Number(item.amount || 0),
    }));
    const allCharges = [...normalizedAR, ...normalizedAP];
    const uniqueContTypes = [...new Set(allCharges.map((i) => i.container))];
    const grouped = {};
    uniqueContTypes.forEach((cont) => {
      grouped[cont] = {
        AR: allCharges.filter((i) => i.container === cont && i.type === 'AR'),
        AP: allCharges.filter((i) => i.container === cont && i.type === 'AP'),
      };
    });
    setContainerTypes(uniqueContTypes);
    setChargesByContainer(grouped);
  };

  /* ── Print / PDF helpers (unchanged from your file) ─────── */
  const handlePrint = () => {
    const printContent = printRef.current.cloneNode(true);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(
      buildBookingPrintDocumentHtml(printContent.outerHTML, formData?.fileNumber || 'Booking', HEADER_BG)
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 900);
  };

  const waitImagesLoaded = async (root) => {
    const images = root.querySelectorAll('img');
    await Promise.all(Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve; img.onerror = resolve; setTimeout(resolve, 2000);
      });
    }));
  };
  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const resolveImageRequestUrl = (src) => {
    if (!src || src.startsWith('data:')) return src;
    const t = String(src).trim();
    if (/^https?:\/\//i.test(t)) return t;
    if (t.startsWith('//')) return `${window.location.protocol}${t}`;
    if (t.startsWith('/')) { try { return new URL(t, window.location.href).href; } catch { } }
    const base = baseURL.live.replace(/\/?$/, '/');
    const path = t.startsWith('/') ? t.slice(1) : t;
    return `${base}${path}`;
  };
  const fetchImageAsDataUrl = async (src) => {
    if (!src || src.startsWith('data:')) return src || null;
    const requestUrl = resolveImageRequestUrl(src);
    const token = localStorage.getItem('authToken');
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const { data } = await axiosInstance({ method: 'get', url: requestUrl, responseType: 'blob', headers: { ...authHeaders } });
      if (data && data.size !== undefined && data.size === 0) return null;
      if (data) return await blobToDataUrl(data);
    } catch { }
    let href = requestUrl;
    try { href = new URL(requestUrl, window.location.href).href; } catch { }
    try {
      const res = await fetch(href, { mode: 'cors', credentials: 'include', headers: { ...authHeaders }, cache: 'force-cache' });
      if (!res.ok) return null;
      return await blobToDataUrl(await res.blob());
    } catch { return null; }
  };
  const tryDrawLiveImageToDataUrl = (liveImg) => {
    if (!liveImg || !liveImg.complete || !(liveImg.naturalWidth > 0)) return null;
    try {
      const c = document.createElement('canvas');
      c.width = liveImg.naturalWidth; c.height = liveImg.naturalHeight;
      c.getContext('2d').drawImage(liveImg, 0, 0);
      return c.toDataURL('image/png');
    } catch { return null; }
  };
  const inlineImagesForBookingPdf = async (cloneRoot, liveRoot) => {
    const cloneImgs = cloneRoot.querySelectorAll('img');
    const liveImgs = liveRoot.querySelectorAll('img');
    await Promise.all(Array.from(cloneImgs).map(async (cImg, i) => {
      const live = liveImgs[i];
      const src = (live && (live.currentSrc || live.getAttribute('src'))) || cImg.getAttribute('src') || '';
      if (!src || src.startsWith('data:')) return;
      let dataUrl = tryDrawLiveImageToDataUrl(live);
      if (!dataUrl) dataUrl = await fetchImageAsDataUrl(src);
      if (dataUrl && String(dataUrl).startsWith('data:')) cImg.setAttribute('src', dataUrl);
    }));
  };
  const scaleCanvasToWidth = (source, targetWidth) => {
    if (!source || source.width === targetWidth) return source;
    const tw = Math.max(1, Math.round(targetWidth));
    const th = Math.max(1, Math.round(source.height * (tw / source.width)));
    const c = document.createElement('canvas');
    c.width = tw; c.height = th;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, source.width, source.height, 0, 0, tw, th);
    return c;
  };
  const captureBookingPdfLayersFromPrintShell = async (printRootOuterHtml, documentTitle) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-12000px;top:0;width:210mm;min-width:794px;height:12000px;min-height:12000px;border:0;opacity:0;pointer-events:none;overflow:auto';
    document.body.appendChild(iframe);
    try {
      const idoc = iframe.contentDocument;
      const win = idoc.defaultView;
      idoc.open();
      idoc.write(buildBookingPrintDocumentHtml(printRootOuterHtml, documentTitle, HEADER_BG));
      idoc.close();
      await new Promise((r) => setTimeout(r, 900));
      await waitImagesLoaded(idoc.body);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const headerCell = idoc.querySelector('.booking-print-header-cell');
      const bodyCell = idoc.querySelector('.booking-print-body-cell');
      const footerCell = idoc.querySelector('.booking-print-footer-repeat-cell');
      if (!headerCell || !bodyCell || !footerCell) throw new Error('Print layout regions not found');
      const scrollH = Math.max(idoc.documentElement?.scrollHeight || 0, idoc.body?.scrollHeight || 0, win?.innerHeight || 0);
      const canvasOpts = { scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: win.innerWidth, windowHeight: scrollH };
      const headerCanvas = await html2canvas(headerCell, canvasOpts);
      const bodyCanvas = await html2canvas(bodyCell, canvasOpts);
      const footerCanvas = await html2canvas(footerCell, canvasOpts);
      const refW = bodyCanvas.width;
      const headerNorm = scaleCanvasToWidth(headerCanvas, refW);
      const footerNorm = scaleCanvasToWidth(footerCanvas, refW);
      return { headerCanvas: headerNorm, bodyCanvas, footerCanvas: footerNorm };
    } finally {
      iframe.remove();
    }
  };

  const handleDownload = async () => {
    if (!printRef.current || !formData) return;
    const downloadBtn = document.querySelector('[data-download-btn]');
    if (downloadBtn) { downloadBtn.textContent = 'Generating PDF…'; downloadBtn.disabled = true; }
    try {
      const printRoot = printRef.current.cloneNode(true);
      await inlineImagesForBookingPdf(printRoot, printRef.current);
      const { headerCanvas, bodyCanvas, footerCanvas } = await captureBookingPdfLayersFromPrintShell(printRoot.outerHTML, formData?.fileNumber || 'Booking');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const mmWidth = pdfWidth - margin * 2;
      const toMm = (c) => c.height * (mmWidth / c.width);
      const headerMm = toMm(headerCanvas);
      const footerMm = toMm(footerCanvas);
      const bodyTotalMm = toMm(bodyCanvas);
      const yFooter = pdfHeight - margin - footerMm + 0.5;
      const yBodyStart = margin + headerMm;
      const availableBodyMm = Math.max(1, yFooter - yBodyStart);
      const pxPerMmBody = bodyCanvas.height / bodyTotalMm;
      const headerData = headerCanvas.toDataURL('image/png');
      const footerData = footerCanvas.toDataURL('image/png');
      let yPx = 0; let pageIndex = 0; const bodyH = bodyCanvas.height;
      while (yPx < bodyH - 0.25) {
        if (pageIndex > 0) pdf.addPage();
        const remainingPx = bodyH - yPx;
        const maxSlicePx = availableBodyMm * pxPerMmBody;
        const slicePx = Math.min(maxSlicePx, remainingPx);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = bodyCanvas.width; sliceCanvas.height = slicePx;
        sliceCanvas.getContext('2d').drawImage(bodyCanvas, 0, yPx, bodyCanvas.width, slicePx, 0, 0, bodyCanvas.width, slicePx);
        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceMm = slicePx * (mmWidth / bodyCanvas.width);
        pdf.addImage(headerData, 'PNG', margin, margin, mmWidth, headerMm);
        pdf.addImage(sliceData, 'PNG', margin, yBodyStart, mmWidth, sliceMm);
        pdf.addImage(footerData, 'PNG', margin, yFooter, mmWidth, footerMm);
        yPx += slicePx; pageIndex++;
      }
      pdf.save(`Booking_${formData.fileNumber || 'Document'}_${moment().format('YYYY-MM-DD')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try printing instead.');
    } finally {
      if (downloadBtn) { downloadBtn.textContent = 'Download PDF'; downloadBtn.disabled = false; }
    }
  };

  const formatDate = (d) => (d && moment(d).isValid() ? moment(d).format('DD MMM YYYY') : '');
  const formatDateTime = (d) => (d && moment(d).isValid() ? moment(d).format('DD MMM YYYY HH:mm') : '');

  if (loading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking data…</p>
        </div>
      </div>
    );
  }

  /* ── Derived display values from formData ───────────────── */
  const company = MasterCompanyDetails?.[0] || {};
  const bookingNo = formData?.carrierBookingNumber;
  const fileNo = formData?.fileNumber;
  const createdDate = formatDate(formData?.createdOn);
  const createdTime = formData?.createdOn && moment(formData.createdOn).isValid() ? moment(formData.createdOn).format('HH:mm') : '';

  const stripPortBracket = (s) => (s ? String(s).split('[')[0].trim() : s);

  const routeOriginSource =
    formData?.loadingLocation && String(formData.loadingLocation).trim()
      ? formData.loadingLocation
      : formData?.ourPlaceOfReceipt;
  const routeOrigin = stripPortBracket(routeOriginSource);

  const buildEquipmentLines = (data) => {
    const lines = [];
    const add = (count, type) => {
      if (!type && !count) return;
      const n = Number(count);
      if (n > 0 && type) lines.push(`${n} × ${type}`);
      else if (type) lines.push(type);
      else if (n > 0) lines.push(String(n));
    };
    add(data?.containerCount, data?.containerType);
    if(data?.containerType1 === "" || data?.containerType1 === "0" || data?.containerCount1 === "0" || data?.containerType1 === "Select") {
      add('');
    }else{

    add(data?.containerCount1, data?.containerType1);}

    if(data?.containerType2 === "" || data?.containerType2 === "0" || data?.containerCount2 === "0" || data?.containerType2 === "Select") {
      add('');
    }else{
    add(data?.containerCount2, data?.containerType2);}
    return lines.map((type) => ({ type }));
  };

  const equipment = buildEquipmentLines(formData);

  const cargoDescription =
    isProvided(formData?.commodity)
      ? formData.commodity
      : isProvided(formData?.cargoDescription)
        ? formData.cargoDescription
        : '';

  const showTruckerOnPrint = !formData?.hideTruckerOnBookingPrint;

  const loadingAddressLinesForRouting = uniqueAddressLines(
    formatAddressLines(formData?.loadingLocationWarehouseAddress),
    formData?.loadingLocationWarehouse,
  );
  const truckerAddressLinesForRouting = uniqueAddressLines(
    formatAddressLines(truckerAddress),
    formData?.trucker,
  );

  const pickupTerminalName =
    formData?.truckerEmptyContainerPickup || stripPortBracket(formData?.ourPlaceOfReceipt);
  const pickupAddressLinesForRouting = uniqueAddressLines(
    formatAddressLines(formData?.truckerEmptyContainerPickupAddress),
    formData?.truckerEmptyContainerPickup,
  );
  const deliveryTerminalName =
    formData?.truckerContainerReturnTo || stripPortBracket(formData?.ourPlaceOfDelivery);
  const deliveryAddressLinesForRouting = uniqueAddressLines(
    formatAddressLines(formData?.truckerContainerReturnToAddress),
    formData?.truckerContainerReturnTo,
  );

  const scheduleItems = [
    { label: 'ETD (POL)', value: formatDateTime(formData?.etsAtPol), hi: true },
    { label: 'ETA (POD)', value: formatDateTime(formData?.etaAtPod), hi: true },
    { label: 'ETA Destination', value: formatDateTime(formData?.etaAtDestination) },
    { label: 'Earliest Return', value: formatDateTime(formData?.earliestRecvdDate) },
    { label: 'Port Cutoff', value: formatDateTime(formData?.portCo) },
    { label: 'DOC Cutoff', value: formatDateTime(formData?.ourDocCo) },
    { label: 'VGM Cutoff', value: formatDateTime(formData?.vgmCo) },
    { label: 'Rail Cutoff', value: formatDateTime(formData?.inlandEailCo) },
    { label: 'Auto Cutoff', value: formatDateTime(formData?.autoCo) },
  ];

  const cargoDimSummary = (formData?.cargoDimensionDetails || [])
    .filter((it) => Number(it?.piece) > 0)
    .map((it) =>
      `${it.piece} ${String(it.packageType || '').toUpperCase()}(S) / ${Number(it.length).toFixed(2)} X ${Number(it.width).toFixed(2)} X ${Number(it.height).toFixed(2)} ${it.dimensionType} / ${Number(it.weightKgs).toFixed(2)} KGS / ${Number(it.cbm).toFixed(2)} CBM`
    );

  return (
    <div className="min-h-screen bg-[#EEF1F5] print:bg-white">
      {/* ── Top action bar (screen-only) ── */}
      <div className="print:hidden sticky top-0 z-20 border-b border-slate-200 bg-[#EEF1F5]/95 px-6 py-2.5 backdrop-blur">
        <div className="mx-auto flex max-w-[860px] items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-[3px]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10.5px] font-bold uppercase tracking-wider text-emerald-700">Confirmed</span>
            </span>
            <span className="text-[12px] text-slate-500">
              Booking <span className="font-semibold text-slate-800">{bookingNo || '—'}</span>
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDownload}
              data-download-btn
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold text-white transition-colors"
              style={{ backgroundColor: NAVY }}
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ── A4 document ── */}
      <div className="flex justify-center px-4 py-6 print:px-0 print:py-0">
        <div
          ref={printRef}
          className="a4-page w-full max-w-[820px] rounded-xl border border-slate-200 bg-white shadow-[0_8px_40px_-12px_rgba(15,39,72,0.18)] print:shadow-none print:rounded-none print:border-0"
          style={{ width: '210mm', minHeight: '297mm' }}
        >
          <table className="booking-print-table w-full" style={{ borderCollapse: 'collapse', width: '100%' }}>
            {/* ── HEADER (repeats on every PDF page) ─────────── */}
            <thead className="booking-print-thead">
              <tr>
                <td className="booking-print-header-cell p-0 align-top" style={{ border: 'none' }}>
                  <div className="avoid-break">
                    {/* Brand bar */}
                    <div className="grid grid-cols-2 gap-4 items-start px-5 pt-3 pb-2 border-b border-slate-100">
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-[2px]">
                          <BadgeCheck className="h-3 w-3 text-emerald-600" />
                          <span className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-emerald-700">Confirmed</span>
                        </div>
                        <h1 className="booking-print-doc-title mt-1.5 text-[20px] font-bold leading-tight tracking-tight" style={{ color: NAVY }}>
                          Booking Confirmation
                        </h1>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          This document confirms shipment{' '}
                          <span className="font-semibold text-slate-700">{bookingNo || '—'}</span>
                          {company?.CompanyName ? <> with <span className="font-semibold text-slate-700">{company.CompanyName}</span>.</> : '.'}
                        </p>
                      </div>

                      <div className="flex flex-col items-end text-right min-w-0">
                        <img
                          src={company?.CompanyLogoDark || company?.CompanyLogo || '/shipez-logo.png'}
                          alt={company?.CompanyName || 'Company Logo'}
                          className="booking-print-header-logo h-9 w-auto object-contain"
                          draggable={false}
                        />
                        <div className="mt-1.5 text-[10.5px] leading-[1.45] text-slate-600 booking-print-header-address">
                          {company?.ProfileAddressPrint
                            ? company.ProfileAddressPrint.split(',').map((line, i) => <div key={i}>{line.trim()}</div>)
                            : null}
                        </div>
                      </div>
                    </div>

                  </div>
                </td>
              </tr>
            </thead>

            {/* ── BODY ───────────────────────────────────────── */}
            <tbody className="booking-print-tbody">
              <tr>
                <td className="booking-print-body-cell p-0 align-top" style={{ border: 'none' }}>
                  <div className="booking-print-gutter space-y-2 px-4 pb-3">
                    {/* Summary strip — first page only (not in repeating header) */}
                    <div className="booking-print-summary-strip rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                      <div className="grid grid-cols-4 gap-x-4 gap-y-2">
                        <ChipMeta label="Booking No." value={bookingNo} mono />
                        <ChipMeta label="File No." value={fileNo} mono />
                        <ChipMeta label="Created" value={createdDate ? `${createdDate} · ${createdTime}` : ''} />
                        <ChipMeta label="Shipping Type" value={formData?.shippingType} />
                      </div>
                    </div>

                    {/* 1. Booking Party + Booking Information */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 print:grid-cols-2 avoid-break">
                      <SectionCard icon={MapPin} title="Booking Party">
                        {isProvided(customerAddress) ? (
                            <>
                          <div className="text-[11px] leading-[1.5] text-slate-700">
                            {String(customerAddress).split(/\r\n|\n/).map((line, i) => (
                              <div key={i} className={i === 0 ? 'text-[12px] font-bold text-slate-900' : ''}>
                                {line}
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 grid grid-cols-1 gap-1 text-[10.5px] text-slate-500">
                              <div>Contact: <span className="font-semibold text-slate-700">{formData?.contactName || '—'}</span></div>
                              <div>Tel: <span className="font-semibold text-slate-700">{formData?.telephone || '—'}</span></div>
                              <div>Email: <span className="font-semibold text-slate-700">{formData?.email || '—'}</span></div>
                            </div>
                            </>
                        ) : (
                          <>
                            <div className="text-[12px] font-bold text-slate-900">{formData?.customerName || '—'}</div>
                            <div className="mt-0.5 text-[11px] leading-[1.5] text-slate-600">
                              {formData?.shipperAddress || ''}
                            </div>
                            <div className="mt-2 grid grid-cols-1 gap-1 text-[10.5px] text-slate-500">
                              <div>Contact: <span className="font-semibold text-slate-700">{formData?.contactName || '—'}</span></div>
                              <div>Tel: <span className="font-semibold text-slate-700">{formData?.telephone || '—'}</span></div>
                              <div>Email: <span className="font-semibold text-slate-700">{formData?.email || '—'}</span></div>
                            </div>
                          </>
                        )}
                      </SectionCard>

                      <SectionCard icon={Receipt} title="Booking Information">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                          <Field label="Booking Status" value="Confirmed" strong />
                          <Field label="Operator" value={formData?.operatorName} />
                          <Field label="Created Date" value={createdDate ? `${createdDate} ${createdTime}` : ''} />
                          <Field label="Shipping Type" value={formData?.shippingType} strong />
                          <Field label="Booking No." value={bookingNo} strong />
                          <Field label="File No." value={fileNo} />
                          <Field label="Customer Ref." value={formData?.customerReference} />
                          <Field label="Office" value={formData?.officeType} />
                        </div>
                      </SectionCard>
                    </div>

                    {/* 2. Routing & Vessel */}
                    <SectionCard icon={Anchor} title="Routing & Vessel Details" subtitle={formData?.carrierMoveType || 'Port-to-port shipment route'}>
                      <RouteTimeline
                        origin={stripPortBracket(formData?.loadingLocation || formData?.ourPlaceOfReceipt)}
                        pol={stripPortBracket(formData?.portOfLoading)}
                        pod={stripPortBracket(formData?.portOfDischarge)}
                        dest={stripPortBracket(formData?.ourPlaceOfDelivery)}
                        carrier={formData?.carrier}
                        vessel={`${formData?.vessel || ''}${formData?.voyage ? ' / ' + formData.voyage : ''}`.trim()}
                        equipment={equipment}
                      />

                      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4">
                        <Field label="Movement Type" value={formData?.carrierMoveType} />
                        <Field label="Door / Origin / Zip" value={routeOrigin} />
                        <Field label="Transshipment Port" value={stripPortBracket(formData?.transhipmentPorts?.[0]?.transPortCity)} />
                        <Field label="Final Destination" value={stripPortBracket(formData?.ourPlaceOfDelivery)} />
                      </div>
                    </SectionCard>

                    {/* 3. Schedule & Cutoffs */}
                    <SectionCard icon={Calendar} title="Schedule & Cutoff Dates" subtitle="Departure, arrival, and key cutoffs">
                      <ScheduleGrid items={scheduleItems} />
                    </SectionCard>

                    {/* 4. Cargo */}
                    <SectionCard icon={Package} title="Cargo Details">
                      <div className="rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2">
                        <div className="text-[8.5px] font-bold uppercase tracking-widest text-slate-500">Description</div>
                        <div className="mt-0.5 text-[12.5px] font-bold text-slate-900 leading-tight">
                          {isProvided(cargoDescription) ? cargoDescription : '—'}
                        </div>
                        {cargoDimSummary.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5 text-[10.5px] text-slate-600">
                            {cargoDimSummary.map((line, i) => <li key={i}>• {line}</li>)}
                          </ul>
                        )}
                      </div>
                      <div className="mt-2.5 space-y-0">
                        <DenseRow label="Hazardous" value={formData?.hazCargo ? 'Yes' : 'No'} strong />
                        <DenseRow label="IMO No." value={formData?.hazImoClass} />
                        <DenseRow label="PKG" value={formData?.hazPkg} />
                        <DenseRow label="UN NBR" value={formData?.hazUnNbr} />
                        <DenseRow
                          label="Reefer Temp."
                          value={
                            formData?.reeferContainerTemperature || formData?.tempUom
                              ? `${formData?.reeferContainerTemperature ?? ''}${formData?.tempUom ?? ''}`
                              : ''
                          }
                        />
                      </div>
                    </SectionCard>

                    <DomesticRoutingPrint
                      pickupTerminal={pickupTerminalName}
                      pickupAddressLines={pickupAddressLinesForRouting}
                      earliestPickupDate={formData?.earliestPickupDate || formData?.truckerEarliestPickup}
                      deliveryTerminal={deliveryTerminalName}
                      deliveryAddressLines={deliveryAddressLinesForRouting}
                      earliestReturnDate={formData?.earliestRecvdDate || formData?.truckerLatestDeliveryDate}
                      loadingName={formData?.loadingLocationWarehouse}
                      loadingAddressLines={loadingAddressLinesForRouting}
                      loadingTel={formData?.loadingLocationWarehouseTelFax}
                      loadingFax=""
                      loadingAttn={formData?.loadingLocationWarehouseContactPerson}
                      loadingEmail=""
                      liveLoad={formatLiveLoadStatus(formData?.loadingStatus)}
                      dropDate={formatRoutingDate(formData?.dropDate)}
                      pickupDate={formatRoutingDate(formData?.pickupDate)}
                      showTrucker={showTruckerOnPrint}
                      truckerName={formData?.trucker}
                      truckerAddressLines={truckerAddressLinesForRouting}
                      truckerCtc={formData?.truckerPoc}
                      truckerTel={formData?.truckerPhone}
                      truckerEmail={formData?.truckerEmail}
                      ctcTrucker={formData?.truckerPoc}
                      rateAgreed={formData?.rateAgreed}
                      rateAgreedForWaiting={formData?.rateAgreedForWaiting}
                    />

                    {/* 8. Charges */}
                    <SectionCard
                      icon={Receipt}
                      title="Charges Summary"
                      subtitle="Accounts receivable (sell) charges"
                    >
                      <ChargesTable
                        containerTypes={containerTypes}
                        chargesByContainer={chargesByContainer}
                        currency={formData?.arCharges?.[0]?.currency || formData?.rates?.[0]?.currency || 'USD'}
                      />
                    </SectionCard>

                    {/* 7. Clauses (only when present) */}
                    {clauses?.length > 0 && (
                      <SectionCard icon={BadgeCheck} title="Clause Sections">
                        <ol className="space-y-1 list-decimal pl-4">
                          {clauses.map((c, i) => (
                            <li key={c.ClauseTypeDescriptionID || i} className="text-[11px] leading-snug text-slate-700">
                              {c.ClauseDescription}
                            </li>
                          ))}
                        </ol>
                      </SectionCard>
                    )}

                    <div className="booking-print-acceptance pt-2">
                      <AcceptanceFooter
                        preparedBy={formData?.createdBy}
                        preparedCo={`${company?.CompanyName || ''}${formData?.officeType ? ' – ' + formData.officeType : ''}`.trim()}
                        date={formatDateTime(formData?.createdOn)}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>

            {/* ── FOOTER: disclaimer only (repeats each PDF page) ── */}
            <tfoot className="booking-print-tfoot">
              <tr>
                <td className="booking-print-footer-repeat-cell p-0 align-top" style={{ border: 'none' }}>
                  <div className="booking-print-gutter px-4 pb-2 pt-1">
                    <PrintDisclaimer poweredBy={MasterCompanyDetails?.[0]?.PoweredBy || 'Shipez.ai'} />
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <style>{`
        .booking-print-gutter { box-sizing: border-box; }
        .booking-print-doc-title { color: ${NAVY}; }
        img.booking-print-header-logo { max-width: 140px; object-fit: contain; }
        .avoid-break { page-break-inside: avoid; break-inside: avoid; }
        .domestic-routing-cell { box-sizing: border-box; }

        @media print {
          @page { size: A4; margin: 8mm; }
          body { margin: 0; padding: 0; background: white; }
          .a4-page, .a4-page * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .a4-page { margin: 0; padding: 0; box-shadow: none; min-height: 0 !important; }
          img.booking-print-header-logo {
            max-width: 132px !important; max-height: 48px !important;
            width: auto !important; height: auto !important; object-fit: contain !important;
          }
          .booking-print-table { width: 100%; border-collapse: collapse; }
          thead.booking-print-thead { display: table-header-group; }
          tfoot.booking-print-tfoot { display: table-footer-group; }
          .print\\:hidden { display: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:border-0 { border-width: 0 !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .print\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          .print\\:col-span-2 { grid-column: span 2 / span 2 !important; }
          .booking-print-acceptance { page-break-inside: avoid; break-inside: avoid; }
          .booking-print-disclaimer { margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default BookingPrint;